/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AbbrechenKontakteAufgabeRequest } from '../models/AbbrechenKontakteAufgabeRequest';
import type { AbbrechenKontakteAufgabeResponse } from '../models/AbbrechenKontakteAufgabeResponse';
import type { KontaktaufnahmeAuswertungDownloadRequest } from '../models/KontaktaufnahmeAuswertungDownloadRequest';
import type { KontaktaufnahmenListe } from '../models/KontaktaufnahmenListe';
import type { KontaktaufnahmeTranskript } from '../models/KontaktaufnahmeTranskript';
import type { RescheduleKontaktaufnahmeRequest } from '../models/RescheduleKontaktaufnahmeRequest';
import type { SetKontaktaufnahmeTwilioIdRequest } from '../models/SetKontaktaufnahmeTwilioIdRequest';
import type { SetKontaktaufnahmeUltravoxIdRequest } from '../models/SetKontaktaufnahmeUltravoxIdRequest';
import type { SuccessResponse } from '../models/SuccessResponse';
import type { ViewKontaktListe } from '../models/ViewKontaktListe';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class KontaktaufnahmeService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Löst fällige Kontaktaufnahmen aus
   * Prüft, ob eine Kontaktaufnahme im Status "geplant" ist und fällig ist.
   * @returns any Erfolgreich geprüft und ggf. Kontaktaufnahmen ausgelöst.
   * @throws ApiError
   */
  public kontaktaufnahmeStart(): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/intern/kontaktaufnahme/start',
      errors: {
        500: `Interner Serverfehler beim Prüfen oder Verarbeiten der Kontaktaufnahmen.`,
      },
    });
  }

  /**
   * Stoppt einen Anruf
   * Der Anruf wird beendet
   * @returns void
   * @throws ApiError
   */
  public stopTwilioCall({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung`,
        404: `Keine der angegebenen Kontaktaufnahmen gefunden`,
        500: `Interner Serverfehler beim Stoppen des Anrufs`,
      },
    });
  }

  /**
   * Markiert eine Kontaktaufnahme als gelöscht
   * Setzt den Status einer Kontaktaufnahme auf DELETED
   * @returns SuccessResponse Kontaktaufnahme erfolgreich als gelöscht markiert
   * @throws ApiError
   */
  public deleteKontaktaufnahme({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Plant eine Kontaktaufnahme neu
   * Erstellt eine neue geplante Kontaktaufnahme basierend auf einer bestehenden Kontaktaufnahme mit optionalem neuem Startzeitpunkt
   * @returns SuccessResponse Kontaktaufnahme erfolgreich neu geplant
   * @throws ApiError
   */
  public rescheduleKontaktaufnahme({
    kontaktaufnahmeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme, die neu geplant werden soll
     */
    kontaktaufnahmeId: string,
    requestBody?: RescheduleKontaktaufnahmeRequest,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/reschedule',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Nicht autorisiert`,
        403: `Fehlende Berechtigung`,
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Setzt die Ultravox-ID einer Kontaktaufnahme
   * Aktualisiert die Ultravox-Call-ID einer bestehenden Kontaktaufnahme
   * @returns SuccessResponse Ultravox-ID erfolgreich gesetzt
   * @throws ApiError
   */
  public setKontaktaufnahmeUltravoxId({
    kontaktaufnahmeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    requestBody: SetKontaktaufnahmeUltravoxIdRequest,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/ultravox-id',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung`,
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Setzt die Twilio-ID einer Kontaktaufnahme
   * Aktualisiert die Twilio-Call-SID einer bestehenden Kontaktaufnahme
   * @returns SuccessResponse Twilio-ID erfolgreich gesetzt
   * @throws ApiError
   */
  public setKontaktaufnahmeTwilioId({
    kontaktaufnahmeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
    requestBody: SetKontaktaufnahmeTwilioIdRequest,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/twilio-id',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung`,
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Markiert die Einwilligung einer Kontaktaufnahme als nicht erteilt
   * Setzt den Status einer Kontaktaufnahme auf UNVOLLSTAENDIG mit callStatus "declined", da keine Einwilligung erteilt wurde
   * @returns SuccessResponse Einwilligung erfolgreich als nicht erteilt markiert
   * @throws ApiError
   */
  public setKontaktaufnahmeConsentNotGiven({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/consent-not-given',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung`,
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Startet die Auswertung einer Kontaktaufnahme neu
   * Startet die Kette für Transkript und alle Folgepunkte neu. Bestehende Transkripte und Auswertungen werden überschrieben.
   * @returns SuccessResponse Prozess erfolgreich gestartet
   * @throws ApiError
   */
  public transcriptAndAnalyse({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<SuccessResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/transcript-and-analyse',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung (z. B. kontaktaufnahmen:edit oder kontaktaufnahmen:own:edit)`,
        404: `Kontaktaufnahme nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Auswertung für Kontaktaufnahmen herunterladen
   * Erzeugt eine Auswertung für eine oder mehrere Kontaktaufnahmen und liefert diese als Download (PDF).
   * @returns binary Auswertung erfolgreich erstellt.
   * @throws ApiError
   */
  public kontaktaufnahmeAuswertungDownload({
    requestBody,
  }: {
    requestBody: KontaktaufnahmeAuswertungDownloadRequest,
  }): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/kontaktaufnahme/auswertung/download',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Forbidden – fehlende Berechtigung (z. B. kontaktaufnahmen:export oder kontaktaufnahmen:own:export)`,
        404: `Keine der angegebenen Kontaktaufnahmen gefunden`,
        500: `Interner Serverfehler beim Erzeugen der Auswertung`,
      },
    });
  }

  /**
   * Lädt die Audio-Datei einer Kontaktaufnahme herunter
   * Gibt die MP3-Audio-Datei einer Kontaktaufnahme zurück
   * @returns binary Audio-Datei erfolgreich abgerufen
   * @throws ApiError
   */
  public downloadKontaktaufnahmeAudio({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontaktaufnahme/{kontaktaufnahmeId}/audio',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        404: `Kontaktaufnahme oder Audio-Datei nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Kontaktaufnahmen für eine Kontaktaufgabe abrufen
   * Gibt alle Kontaktaufnahmen zurück, die zu einer bestimmten Kontaktaufgabe gehören
   * @returns KontaktaufnahmenListe Liste der Kontaktaufnahmen für die angegebene Kontaktaufgabe
   * @throws ApiError
   */
  public getKontaktaufnahmenByKontaktaufgabeId({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<KontaktaufnahmenListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontaktaufnahme/pro-kontaktaufgabe/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        400: `Ungültige Anfrage`,
        401: `Nicht autorisiert`,
        403: `Fehlende Berechtigung`,
        404: `Kontaktaufgabe nicht gefunden oder keine Kontaktaufnahmen vorhanden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Transkript einer Kontaktaufnahme abrufen
   * Gibt das Transkript der angegebenen Kontaktaufnahme zurück
   * @returns KontaktaufnahmeTranskript Transkript der Kontaktaufnahme
   * @throws ApiError
   */
  public getKontaktaufnahmeTranskript({
    kontaktaufnahmeId,
  }: {
    /**
     * Eindeutige ID der Kontaktaufnahme
     */
    kontaktaufnahmeId: string,
  }): CancelablePromise<KontaktaufnahmeTranskript> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/kontaktaufnahme/transkript/{kontaktaufnahmeId}',
      path: {
        'kontaktaufnahmeId': kontaktaufnahmeId,
      },
      errors: {
        404: `Kontaktaufnahme oder Transkript nicht gefunden`,
        500: `Interner Serverfehler`,
      },
    });
  }

  /**
   * Eine Liste aller Kontakte
   * Gibt eine Liste aller Kontakte zu einer Aufgabe zurück
   * @returns ViewKontaktListe Liste der gesuchten Kontaktdaten zu einer Aufgabe
   * @throws ApiError
   */
  public searchKontakteView({
    aufgabeId,
    search,
    filtered,
    page,
    limit,
    sort,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (searchKontakteView)
     */
    aufgabeId: string,
    /**
     * Suchstring für Kontaktdaten View
     */
    search?: string,
    /**
     * Filterstring für Kontaktdaten View Seite
     */
    filtered?: string,
    /**
     * Seitenzahl der Kontaktdaten View Seite
     */
    page?: number,
    /**
     * Limit der Liste von Kontaktdaten View Einträgen
     */
    limit?: number,
    /**
     * Sortierung der Kontaktdaten View Seite
     */
    sort?: string,
  }): CancelablePromise<ViewKontaktListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/kontakte',
      path: {
        'aufgabeId': aufgabeId,
      },
      query: {
        'search': search,
        'filtered': filtered,
        'page': page,
        'limit': limit,
        'sort': sort,
      },
      errors: {
        400: `Bad Request for this request (searchKontakteView)`,
        401: `Unauthorized for this request (searchKontakteView)`,
        403: `Forbidden for this request (searchKontakteView)`,
        404: `Not Found for this request (searchKontakteView)`,
      },
    });
  }

  /**
   * Kontakte Aufgabe abbrechen
   * Bricht die Kontakte Aufgabe eines Kontaktes innerhalb einer Aufgabe ab. Der Kontakt wird über die kontaktId oder die externalId identifiziert. Alle noch nicht durchgeführten Kontaktaufnahmen werden auf ABGEBROCHEN gesetzt, ebenso die Kontakte Aufgabe selbst.
   *
   * @returns AbbrechenKontakteAufgabeResponse Kontakte Aufgabe erfolgreich abgebrochen
   * @throws ApiError
   */
  public abbrechenKontakteAufgabe({
    aufgabeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (abbrechenKontakteAufgabe)
     */
    aufgabeId: string,
    requestBody: AbbrechenKontakteAufgabeRequest,
  }): CancelablePromise<AbbrechenKontakteAufgabeResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/kontakte/abbrechen',
      path: {
        'aufgabeId': aufgabeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request for this request (abbrechenKontakteAufgabe)`,
        401: `Unauthorized for this request (abbrechenKontakteAufgabe)`,
        403: `Forbidden for this request (abbrechenKontakteAufgabe)`,
        404: `Not Found for this request (abbrechenKontakteAufgabe)`,
      },
    });
  }

}
